import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "asPayFailBanner_row" }
const _hoisted_2 = { class: "asPayFailBanner_message" }
const _hoisted_3 = { class: "asPayFailBanner_titleRow" }
const _hoisted_4 = { class: "asPayFailBanner_title" }
const _hoisted_5 = { class: "asPayFailBanner_subtitle" }
const _hoisted_6 = ["aria-label"]
const _hoisted_7 = { class: "asPayFailBanner_title" }
const _hoisted_8 = {
  class: "asPayFailBanner_details",
  "data-test": "payment-failure-details"
}
const _hoisted_9 = {
  key: 1,
  class: "asPayFailBanner_body"
}
const _hoisted_10 = { class: "asPayFailBanner_header" }
const _hoisted_11 = { class: "asPayFailBanner_titleRow" }
const _hoisted_12 = { class: "asPayFailBanner_title" }
const _hoisted_13 = ["aria-label"]
const _hoisted_14 = { class: "asPayFailBanner_subtitle" }
const _hoisted_15 = { class: "asPayFailBanner_actions" }

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useMq } from 'vue3-mq'
import usePaymentFailureWarning from '@web/platform.v3/components/payment-failure/usePaymentFailureWarning'
import { useCookieConsent } from '@web/platform.v3/composables/useCookieConsent'

const OFFSET_VAR = '--as-payment-warning-offset'

export default /*@__PURE__*/_defineComponent({
  ...{ name: 'PaymentFailureBanner' },
  __name: 'PaymentFailureBanner',
  setup(__props) {

// onMounted / onBeforeUnmount / ref / nextTick понадобятся в задаче 6 —
// импортируем сразу, чтобы не переписывать шапку дважды.


const mq = useMq()
const isDesktop = computed<boolean>(() => mq.current === 'desktop')

const {
  isVisible, isExpanded, canResume, canDecline,
  onResume, onDecline, onClose, onExpand, trackView
} = usePaymentFailureWarning()

// Десктопный блок действий резервирует фиксированную ширину под обе кнопки
// (см. стили). Если контракт не разрешил ни одну — резервировать нечего,
// иначе рядом с «×» остаётся ~480px пустоты.
const isActionsBare = computed<boolean>(() => !canResume.value && !canDecline.value)

// Событие показа: раньше оно уходило из onMounted, но плашка теперь монтируется
// в App.vue раньше, чем загружаются подписки, — на момент монтирования
// isVisible ещё ложен, и событие не ушло бы никогда.
//
// viewTracked — защита от повторной отправки: при immediate: true колбэк с уже
// истинным isVisible выполняется синхронно внутри самого вызова watch(), до
// того как переменной stopViewWatch успевает присвоиться возвращаемая функция
// остановки (без заглушки — ReferenceError, temporal dead zone). В этот момент
// stopViewWatch ещё указывает на плейсхолдер-no-op, поэтому реальный watcher
// остался бы висеть живым, и повторный переход isVisible false → true отправил
// бы trackView() второй раз. viewTracked гарантирует ровно один вызов
// независимо от того, наступил показ сразу при монтировании или позже; строка
// после watch() довызывает уже настоящий stopViewWatch, если колбэк успел
// сработать в immediate-фазе.
let viewTracked = false
let stopViewWatch: () => void = () => {}
stopViewWatch = watch(isVisible, visible => {
  if (!visible || viewTracked) return
  viewTracked = true
  trackView()
  stopViewWatch()
}, { immediate: true })
if (viewTracked) stopViewWatch()

// Cookie-consent тоже прижат к низу окна и лежит выше по слою: пока он висит,
// островок поднимаем над ним, иначе его не видно целиком и «×» недоступен.
const { isVisible: isConsentVisible } = useCookieConsent()

const root = ref<HTMLElement | null>(null)
let observer: ResizeObserver | undefined

// Отступ нужен только мобильной полоске: она fixed сверху и без него легла бы
// поверх шапки. Десктопный островок висит внизу поверх контента — по ТЗ так и надо.
function writeOffset (): void {
  if (!isVisible.value || isDesktop.value || !root.value) {
    document.documentElement.style.removeProperty(OFFSET_VAR)
    return
  }
  document.documentElement.style.setProperty(OFFSET_VAR, `${root.value.offsetHeight}px`)
}

onMounted(() => {
  writeOffset()
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(writeOffset)
    if (root.value) observer.observe(root.value)
  }
})

watch([isVisible, isDesktop, isExpanded], async () => {
  await nextTick()
  writeOffset()
  // disconnect() перед observe(): при переключении раскладки через 1244px
  // DOM-узел за ref="root" заменяется, а без отсоединения старого узла
  // наблюдатель держит на него сильную ссылку до размонтирования компонента —
  // которое, раз он теперь один на всё приложение, попросту не наступает.
  if (observer && root.value) {
    observer.disconnect()
    observer.observe(root.value)
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
  document.documentElement.style.removeProperty(OFFSET_VAR)
})

return (_ctx: any,_cache: any) => {
  return (_unref(isVisible) && isDesktop.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "root",
        ref: root,
        class: _normalizeClass(["asPayFailBanner asPayFailBanner--desktop", { 'asPayFailBanner--aboveConsent': _unref(isConsentVisible) }]),
        "data-test": "payment-failure-banner"
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.$t('paymentFailure.title')), 1),
              _cache[7] || (_cache[7] = _createElementVNode("span", {
                class: "asPayFailBanner_icon",
                "aria-hidden": "true"
              }, [
                _createElementVNode("svg", {
                  width: "16",
                  height: "16",
                  viewBox: "0 0 16 16",
                  fill: "none",
                  xmlns: "http://www.w3.org/2000/svg"
                }, [
                  _createElementVNode("path", {
                    "fill-rule": "evenodd",
                    "clip-rule": "evenodd",
                    fill: "currentColor",
                    d: "M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8Zm0 3.4a.9.9 0 0 1 .9.95l-.25 4.2a.65.65 0 0 1-1.3 0l-.25-4.2A.9.9 0 0 1 8 3.4Zm0 7.1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
                  })
                ])
              ], -1))
            ]),
            _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t('paymentFailure.subtitle')), 1)
          ]),
          _createElementVNode("div", {
            class: _normalizeClass(["asPayFailBanner_actions", { 'asPayFailBanner_actions--bare': isActionsBare.value }])
          }, [
            (_unref(canDecline))
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "asPayFailBanner_decline",
                  "data-test": "payment-failure-decline",
                  onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(onDecline) && _unref(onDecline)(...args)))
                }, _toDisplayString(_ctx.$t('paymentFailure.declineBtn')), 1))
              : _createCommentVNode("", true),
            (_unref(canResume))
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  type: "button",
                  class: "asPayFailBanner_resume",
                  "data-test": "payment-failure-resume",
                  onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(onResume) && _unref(onResume)(...args)))
                }, _toDisplayString(_ctx.$t('superSubscription.status.resumeBtn')), 1))
              : _createCommentVNode("", true),
            _createElementVNode("button", {
              type: "button",
              class: "asPayFailBanner_close",
              "data-test": "payment-failure-close",
              "aria-label": _ctx.$t('close'),
              onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(onClose) && _unref(onClose)(...args)))
            }, [...(_cache[8] || (_cache[8] = [
              _createElementVNode("svg", {
                width: "20",
                height: "20",
                viewBox: "0 0 20 20",
                fill: "none",
                xmlns: "http://www.w3.org/2000/svg"
              }, [
                _createElementVNode("path", {
                  d: "M5 5L15 15",
                  stroke: "currentColor",
                  "stroke-width": "1.5",
                  "stroke-linecap": "round"
                }),
                _createElementVNode("path", {
                  d: "M15 5L5 15",
                  stroke: "currentColor",
                  "stroke-width": "1.5",
                  "stroke-linecap": "round"
                })
              ], -1)
            ]))], 8, _hoisted_6)
          ], 2)
        ])
      ], 2))
    : (_unref(isVisible))
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          ref_key: "root",
          ref: root,
          class: "asPayFailBanner asPayFailBanner--mobile",
          "data-test": "payment-failure-banner"
        }, [
          (!_unref(isExpanded))
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                class: "asPayFailBanner_strip",
                "data-test": "payment-failure-strip",
                onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(onExpand) && _unref(onExpand)(...args)))
              }, [
                _cache[9] || (_cache[9] = _createElementVNode("span", {
                  class: "asPayFailBanner_icon",
                  "aria-hidden": "true"
                }, [
                  _createElementVNode("svg", {
                    width: "16",
                    height: "16",
                    viewBox: "0 0 16 16",
                    fill: "none",
                    xmlns: "http://www.w3.org/2000/svg"
                  }, [
                    _createElementVNode("path", {
                      "fill-rule": "evenodd",
                      "clip-rule": "evenodd",
                      fill: "currentColor",
                      d: "M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8Zm0 3.4a.9.9 0 0 1 .9.95l-.25 4.2a.65.65 0 0 1-1.3 0l-.25-4.2A.9.9 0 0 1 8 3.4Zm0 7.1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
                    })
                  ])
                ], -1)),
                _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t('paymentFailure.title')), 1),
                _createElementVNode("span", _hoisted_8, _toDisplayString(_ctx.$t('paymentFailure.detailsLink')), 1)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, [
                    _cache[11] || (_cache[11] = _createElementVNode("span", {
                      class: "asPayFailBanner_icon",
                      "aria-hidden": "true"
                    }, [
                      _createElementVNode("svg", {
                        width: "16",
                        height: "16",
                        viewBox: "0 0 16 16",
                        fill: "none",
                        xmlns: "http://www.w3.org/2000/svg"
                      }, [
                        _createElementVNode("path", {
                          "fill-rule": "evenodd",
                          "clip-rule": "evenodd",
                          fill: "currentColor",
                          d: "M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8Zm0 3.4a.9.9 0 0 1 .9.95l-.25 4.2a.65.65 0 0 1-1.3 0l-.25-4.2A.9.9 0 0 1 8 3.4Zm0 7.1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
                        })
                      ])
                    ], -1)),
                    _createElementVNode("span", _hoisted_12, _toDisplayString(_ctx.$t('paymentFailure.title')), 1),
                    _createElementVNode("button", {
                      type: "button",
                      class: "asPayFailBanner_close",
                      "data-test": "payment-failure-close",
                      "aria-label": _ctx.$t('close'),
                      onClick: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(onClose) && _unref(onClose)(...args)))
                    }, [...(_cache[10] || (_cache[10] = [
                      _createElementVNode("svg", {
                        width: "20",
                        height: "20",
                        viewBox: "0 0 20 20",
                        fill: "none",
                        xmlns: "http://www.w3.org/2000/svg"
                      }, [
                        _createElementVNode("path", {
                          d: "M5 5L15 15",
                          stroke: "currentColor",
                          "stroke-width": "1.5",
                          "stroke-linecap": "round"
                        }),
                        _createElementVNode("path", {
                          d: "M15 5L5 15",
                          stroke: "currentColor",
                          "stroke-width": "1.5",
                          "stroke-linecap": "round"
                        })
                      ], -1)
                    ]))], 8, _hoisted_13)
                  ]),
                  _createElementVNode("p", _hoisted_14, _toDisplayString(_ctx.$t('paymentFailure.subtitle')), 1)
                ]),
                _createElementVNode("div", _hoisted_15, [
                  (_unref(canDecline))
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        type: "button",
                        class: "asPayFailBanner_decline",
                        "data-test": "payment-failure-decline",
                        onClick: _cache[5] || (_cache[5] = 
//@ts-ignore
(...args) => (_unref(onDecline) && _unref(onDecline)(...args)))
                      }, _toDisplayString(_ctx.$t('paymentFailure.declineBtn')), 1))
                    : _createCommentVNode("", true),
                  (_unref(canResume))
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        type: "button",
                        class: "asPayFailBanner_resume",
                        "data-test": "payment-failure-resume",
                        onClick: _cache[6] || (_cache[6] = 
//@ts-ignore
(...args) => (_unref(onResume) && _unref(onResume)(...args)))
                      }, _toDisplayString(_ctx.$t('superSubscription.status.resumeBtn')), 1))
                    : _createCommentVNode("", true)
                ])
              ]))
        ], 512))
      : _createCommentVNode("", true)
}
}

})